import { Box, Separator, Flex, Spacer } from '@chakra-ui/react'
import type { FC } from 'react'
import { memo, useState, useMemo } from 'react'

import { Button } from '@app/components/ui/button'
import ChatDeleteButton from '@app/pages/strategies/components/aiChat/components/chatDeleteButton'
import ChatsListMessage from '@app/pages/strategies/components/aiChat/components/chatsListMessage'
import ContextLink from '@app/pages/strategies/components/aiChat/components/contextLink'
import useGetChattableChats from '@app/pages/strategies/components/aiChat/useGetChattableChats'
import DrawerCloseButton from '@app/shared/drawer/drawerCloseButton'

type Props = {
  chattableType: string
  chattableId: string
  inDrawer?: boolean
}

const Item = ({ chat }) => {
  const [hovered, setHovered] = useState(false)

  const onMouseEnter = () => {
    setHovered(true)
  }

  const onMouseLeave = () => {
    setHovered(false)
  }

  return (
    <Box key={chat.id} pos="relative" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <ContextLink to={['show', chat.id]}>
        <ChatsListMessage messageId={chat.mostRecentPrompt.id} />
      </ContextLink>
      {hovered && (
        <Flex pos="absolute" zIndex="1" top="0" right="0" bottom="0" align="center">
          <ChatDeleteButton chat={chat} />
        </Flex>
      )}
    </Box>
  )
}

const MemoItem = memo(Item)

const List: FC<Props> = ({ chattableType, chattableId, inDrawer = true }) => {
  const chats = useGetChattableChats(chattableType, chattableId)

  const sorted = useMemo(
    () =>
      chats.sort((a, b) => {
        const aDate = new Date(a.mostRecentPrompt.createdAt)
        const bDate = new Date(b.mostRecentPrompt.createdAt)

        return aDate > bDate ? 1 : -1
      }),
    [chats]
  )

  return (
    <Flex pos="relative" direction="column" overflow="hidden" h="100%">
      <Flex p={2}>
        <Spacer />
        {inDrawer && <DrawerCloseButton variant="ghost" />}
      </Flex>
      <Box overflowY="auto">
        {sorted.map((chat) => (
          <MemoItem key={chat.id} chat={chat} />
        ))}
      </Box>
      <Separator />
      <Button asChild m={4}>
        <ContextLink to="new">Start a new conversation</ContextLink>
      </Button>
    </Flex>
  )
}

export default memo(List)
